import {States} from "../../../types";
import {useNavigate} from "react-router-dom";
import {useCreateNegotiationStatusTransitionMutation,} from "../../../redux/api/negotiationsApiSlice";
import React, {Dispatch, Fragment, SetStateAction, useCallback, useEffect, useMemo, useState} from "react";
import {FormInstance, message, Modal, Popover} from "antd";
import {NegotiationFormModel, NegotiationModel, UpdateNegotiationRequest} from "../../../types/negotiations";
import {CompleteNegotiationStatusView, NegotiationStatusViewBaseProps} from "./status/CompleteNegotiationStatusView";
import {ContractorDataFormModel} from "../../../types/contractors";
import {DuplicatedNegotiationProvider} from "../../../context/DuplicatedNegotiationContext";
import {QuestionCircleOutlined} from "@ant-design/icons";

export function getSuggestion(value?: string[] | { value: string, source: string | null }[]) {
  if (!value || value.length === 0) return;

  if (typeof value[0] === "string") {
    const warningText = `Dati trovati da AI: ${value?.join(', ')}`;

    return <> {warningText} </>;
  }

  const valueObject = value.map((v, i) => {
    const castedV = v as { value: string, source: string | null };

    return <Fragment key={i}>
      {castedV.value}{' '}
      { !!castedV.source && (
        <>
          <Popover
            placement="bottom"
            content={`Fonte: ${(v as { value: string, source: string }).source}`}
          >
            <QuestionCircleOutlined/>
          </Popover>
          {i < value.length - 1 && ', '}
        </>
      )}
    </Fragment>
  });

  return (
    <>
      Dati trovati da AI:{' '}
      { valueObject }
    </>
  );
}

export type NegotiationDetailStatusPropsType = {
    negotiation: NegotiationModel,
    setNegotiation: (negotiation: NegotiationModel) => void
    updateNegotiation: (data: Partial<UpdateNegotiationRequest>) => Promise<NegotiationModel | undefined>
    form: FormInstance<NegotiationFormModel>,
    contractorDataForm: FormInstance<ContractorDataFormModel>,
    setIsFormDisabled: Dispatch<SetStateAction<boolean>>,
    goToDocuments: () => void,
    isRoreSuggestionsVisible: boolean,
    setIsRoreSuggestionsVisible: (value: boolean) => void,
}

export function NegotiationDetailStatusManager({
                                                   negotiation,
                                                   setNegotiation,
                                                   updateNegotiation,
                                                   form,
                                                   contractorDataForm,
                                                   setIsFormDisabled,
                                                   goToDocuments,
                                                   isRoreSuggestionsVisible,
                                                   setIsRoreSuggestionsVisible,
                                               }: NegotiationDetailStatusPropsType) {

    const navigate = useNavigate();

    const [createStateTransition] = useCreateNegotiationStatusTransitionMutation();

    const [
        formFieldErrors,
        setFormFieldErrors
    ] = useState<{
        field: string | [string, number, string],
        error: string
    }[]>()
    const [
        isErrorStateTransitionModalOpen,
        setIsErrorStateTransitionModalOpen
    ] = useState<boolean>(false);
    const [
        isStateTransitionInProgress,
        setIsStateTransitionInProgress
    ] = useState<boolean>(false);
    const [
        uuidToGoTo,
        setUuidToGoTo
    ] = useState<string | null>(null);

    const resetErrors = useCallback((specificForm: FormInstance) => {
        specificForm
            .getFieldsError()
            .filter((el: any) => el.errors.length)
            .forEach((el: any) => {
                specificForm.setFields([
                    {
                        name: el.name,
                        errors: []
                    },
                ])
            })
    }, [])

    const resetWarnings = useCallback((form: FormInstance) => {
        form.getFieldsError()
            .filter((el: any) => el.warnings.length)
            .forEach((el: any) => {
                form.setFields([
                    {
                        name: el.name,
                        warnings: []
                    },
                ])
            })
    }, [])

    /*
    * newNegotiation viene usato da AssignNegotiationModal*/
    async function handleCreateStateTransition(data: {
        state_to: number,
        reason?: string,
        notes?: string
    }, newNegotiation?: NegotiationModel) {
        setIsStateTransitionInProgress(true);
        setIsFormDisabled(true);
        try {
            const createTransitionResult = await createStateTransition({id: negotiation.uuid, data: data}).unwrap();
            setUuidToGoTo(createTransitionResult.new_negotiation_uuid);
            setNegotiation({
                ...(newNegotiation || negotiation),
                is_portfolio_managed: createTransitionResult.is_portfolio_managed,
                state: createTransitionResult.state_to,
                available_state_transitions: createTransitionResult.available_states,
                revert_state_transition: createTransitionResult.revert_state_transition
            });
            if (createTransitionResult.state_from === States.Rore) {
                hideRoreSuggestions()
            }
            resetErrors(form);
            resetErrors(contractorDataForm);
            message.success('Stato aggiornato');
        } catch (e: any) {
            message.error('Impossibile aggiornare lo stato della trattativa');
            console.error('createStateTransition', e);
            if (Array.isArray(e.data)) {
                setIsErrorStateTransitionModalOpen(true);
                setFormFieldErrors(e.data);
            }
        } finally {
            setIsStateTransitionInProgress(false);
            setIsFormDisabled(false);
        }
    }

    async function updateNegotiationUnderwriter(underwriterId: string): Promise<NegotiationModel | undefined> {
        try {
            return await updateNegotiation({underwriter: underwriterId})
        } catch (e: any) {
            message.error('Impossibile assegnare l\'underwriter')
            console.error('updateUnderwriter')
        }
    }

    useEffect(() => { // set the focus on fields that have to be fixed to complete a state transition
        resetErrors(form)
        resetErrors(contractorDataForm)
        formFieldErrors?.forEach(el => {
            //CONTRACTOR FORM ERRORS
            if (el.field === 'province') {
                contractorDataForm.setFields([
                    {
                        name: el.field,
                        errors: [el.error]
                    },
                ])
            } else {
                //NEGOTIATION FORM ERRORS
                form.setFields([
                    {
                        name: el.field,
                        errors: [el.error]
                    },
                ])
            }
        })
    }, [contractorDataForm, form, formFieldErrors, resetErrors])


    const showRoreSuggestions = useCallback(() => {
        if (!negotiation.raw_data) return;

        Object.entries(negotiation.raw_data).forEach(([key, value]) => {
            if (value.length === 0) return;

            // Verifico se il campo esiste nel form del contractor
            const isContractorField = contractorDataForm.getFieldValue([key]) !== undefined;
            const targetForm = isContractorField ? contractorDataForm : form;

            const suggestionText = getSuggestion(value);

            targetForm.setFields([{
                name: key,
                warnings: [suggestionText as unknown as string]
            }]);
        });

        setIsRoreSuggestionsVisible(true);
    }, [form, contractorDataForm, negotiation.raw_data]);


    const hideRoreSuggestions = useCallback(() => {
        // reset warnings su entrambi i form (negotiation e contractor)
        [form, contractorDataForm].forEach(f =>
            f.getFieldsError()
                .filter(el => el.warnings.length)
                .forEach(el =>
                    f.setFields([{name: el.name, warnings: []}])
                )
        );
        setIsRoreSuggestionsVisible(false);
    }, [form, contractorDataForm]);

    useEffect(() => {
        if (negotiation.state === States.Rore) {
            showRoreSuggestions()
        }
    }, [negotiation.state, showRoreSuggestions])



    const viewProps: NegotiationStatusViewBaseProps = useMemo(()=> ({
        negotiation: negotiation,
        createStateTransition: handleCreateStateTransition,
        isStateTransitionInProgress: isStateTransitionInProgress,
        updateNegotiation: updateNegotiation,
        updateNegotiationUw: updateNegotiationUnderwriter,
        goToNewNegotiation: uuidToGoTo ? (() => {
            navigate(`/negotiations/${uuidToGoTo}`);
            setUuidToGoTo(null);
        }) : null,
        goToDocuments: goToDocuments,
        showRoreSuggestions,
        hideRoreSuggestions,
        isRoreSuggestionsVisible,
    }), [goToDocuments, handleCreateStateTransition, hideRoreSuggestions, isRoreSuggestionsVisible, isStateTransitionInProgress, navigate, negotiation, showRoreSuggestions, updateNegotiation, updateNegotiationUnderwriter, uuidToGoTo])

    return (
        <>
            <DuplicatedNegotiationProvider>
                <CompleteNegotiationStatusView
                    {...viewProps}/>
            </DuplicatedNegotiationProvider>
            <Modal
                open={isErrorStateTransitionModalOpen}
                title={<p style={{color: "red"}}>Impossibile aggiornare lo stato della trattativa</p>}
                footer={null}
                onCancel={() => setIsErrorStateTransitionModalOpen(false)}>
                <p style={{paddingBottom: "100px"}}>
                    Per finalizzare il passaggio di stato risolvere i seguenti errori:</p>
                <ul>
                    {formFieldErrors?.map((el, index) => (
                        <li
                            key={index}
                            style={{
                                marginTop: "0.75em",
                                marginLeft: "0.85em"
                            }}>
                            {Array.isArray(el.error) ? el.error.join('_') : el.error}
                        </li>)
                    )}
                </ul>
                <p>
                    I campi interessati dall'errore sono evidenziati in rosso nel form e l'errore verrà rimosso una
                    volta
                    effettuato il passaggio di stato
                </p>
            </Modal>
        </>
    )
}